import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';

// 科技感动画
const glowPulse = keyframes`
  0%, 100% {
    text-shadow: 
      0 0 10px rgba(0, 255, 255, 0.8),
      0 0 20px rgba(0, 255, 255, 0.6),
      0 0 30px rgba(0, 255, 255, 0.4),
      0 0 40px rgba(0, 255, 255, 0.2);
  }
  50% {
    text-shadow: 
      0 0 20px rgba(0, 255, 255, 1),
      0 0 30px rgba(0, 255, 255, 0.8),
      0 0 40px rgba(0, 255, 255, 0.6),
      0 0 50px rgba(0, 255, 255, 0.4);
  }
`;

const scanLine = keyframes`
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
`;

const glitch = keyframes`
  0%, 100% {
    transform: translate(0);
    filter: hue-rotate(0deg);
  }
  20% {
    transform: translate(-2px, 2px);
    filter: hue-rotate(90deg);
  }
  40% {
    transform: translate(-2px, -2px);
    filter: hue-rotate(180deg);
  }
  60% {
    transform: translate(2px, 2px);
    filter: hue-rotate(270deg);
  }
  80% {
    transform: translate(2px, -2px);
    filter: hue-rotate(360deg);
  }
`;

// 主标题容器
const TechTitleContainer = styled(motion.div)`
  position: relative;
  display: inline-block;
  
  /* 扫描线效果 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(0, 255, 255, 0.8), 
      transparent
    );
    animation: ${scanLine} 3s ease-in-out infinite;
    z-index: 10;
  }
  
  /* 底部扫描线 */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(0, 255, 255, 0.6), 
      transparent
    );
    animation: ${scanLine} 3s ease-in-out infinite reverse;
    z-index: 10;
  }
`;

// 主标题文字
const TechTitle = styled.h1`
  font-family: ${({ theme }) => theme.fontFamily.merged};
  font-size: ${({ theme }) => theme.fontSize['6xl']};
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  line-height: ${({ theme }) => theme.lineHeight.tighter};
  letter-spacing: ${({ theme }) => theme.letterSpacing.wider};
  text-transform: uppercase;
  margin: 0;
  position: relative;
  z-index: 2;
  
  /* 渐变文字效果 */
  background: linear-gradient(
    135deg,
    #00ffff 0%,
    #0099ff 25%,
    #0066ff 50%,
      #00ffff 75%,
    #00ccff 100%
  );
  background-size: 200% 200%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  
  /* 动画效果 */
  animation: 
    ${glowPulse} 2s ease-in-out infinite,
    glitch 4s ease-in-out infinite;
  
  /* 背景动画 */
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  animation: 
    ${glowPulse} 2s ease-in-out infinite,
    glitch 4s ease-in-out infinite,
    gradientShift 3s ease-in-out infinite;
  
  /* 响应式设计 */
  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.fontSize['4xl']};
    letter-spacing: ${({ theme }) => theme.letterSpacing.wide};
  }
  
  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.fontSize['3xl']};
    letter-spacing: ${({ theme }) => theme.letterSpacing.normal};
  }
`;

// 副标题
const TechSubtitle = styled(motion.p)`
  font-family: ${({ theme }) => theme.fontFamily.gradient};
  font-size: ${({ theme }) => theme.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  letter-spacing: ${({ theme }) => theme.letterSpacing.wider};
  text-transform: uppercase;
  margin: 1rem 0 0 0;
  
  /* 渐变效果 */
  background: linear-gradient(
    90deg,
    #00ffff 0%,
    #0099ff 50%,
    #00ccff 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  
  /* 动画 */
  animation: gradientShift 2s linear infinite;
  
  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.fontSize.xl};
  }
`;

// 装饰性元素
const DecorativeElements = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 120%;
  height: 120%;
  pointer-events: none;
  z-index: 1;
  
  /* 角落装饰 */
  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border: 2px solid rgba(0, 255, 255, 0.6);
  }
  
  &::before {
    top: -10px;
    left: -10px;
    border-right: none;
    border-bottom: none;
  }
  
  &::after {
    bottom: -10px;
    right: -10px;
    border-left: none;
    border-top: none;
  }
`;

// 数据流动效果
const DataFlow = styled.div`
  position: absolute;
  top: 0;
  left: -100px;
  width: 100px;
  height: 100%;
  overflow: hidden;
  opacity: 0.3;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 2px;
    height: 100%;
    background: linear-gradient(
      180deg,
      transparent 0%,
      #00ffff 50%,
      transparent 100%
    );
    animation: dataFlow 2s linear infinite;
  }
  
  @keyframes dataFlow {
    0% {
      transform: translateY(-100%);
    }
    100% {
      transform: translateY(100%);
    }
  }
`;

const TechHeroTitle = ({ 
  title = "SenseAI", 
  subtitle = "Privacy-First Contactless Sensing",
  className = "",
  enableGlitch = true,
  enableDataFlow = true 
}) => {
  return (
    <TechTitleContainer
      className={className}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ 
        duration: 1.2,
        ease: "easeOut"
      }}
    >
      {enableDataFlow && <DataFlow />}
      
      <DecorativeElements />
      
      <TechTitle
        style={{
          animationPlayState: enableGlitch ? 'running' : 'paused'
        }}
      >
        {title}
      </TechTitle>
      
      {subtitle && (
        <TechSubtitle
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.8,
            delay: 0.5,
            ease: "easeOut"
          }}
        >
          {subtitle}
        </TechSubtitle>
      )}
    </TechTitleContainer>
  );
};

export default TechHeroTitle;